import autobind from "autobind-decorator";
import type { InputHTMLAttributes, ReactNode } from "react";
import type { FieldRenderProps } from "react-final-form";
import { Console } from "@/shared/lib/console";
import { PromisePool } from "@/shared/ui";
import { BmMask } from "@/shared/ui/mask";
import type { FormInputProps, FormInputState } from "./FormInput";
import { FormInput } from "./FormInput";

export interface FormMaskedInputProps<Type = string> extends FormInputProps<Type> {
	/**
	 * Optional input mask. Can be a mask pattern string or a function that transforms the input text.
	 */
	mask?: string | ((text: string) => string);
}

export interface FormMaskedInputState<Type> extends FormInputState {
	selectionStart: number;
	selectionEnd: number;
	lastValue: Type;
	lastKey: number;
}

export class FormMaskedInput<
	Type = string,
	Props extends FormMaskedInputProps<Type> = FormMaskedInputProps<Type>,
	State extends FormMaskedInputState<Type> = FormMaskedInputState<Type>,
> extends FormInput<Type, Props, State> {
	private readonly promisePool = new PromisePool();
	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			selectionStart: undefined,
			selectionEnd: undefined,
			lastValue: undefined,
		} as unknown as State;
	}

	public componentDidUpdate(prevProps: Props, prevState: State): void {
		if (
			this.inputElRef?.current &&
			typeof this.inputElRef.current.setSelectionRange === "function" &&
			document.activeElement === this.inputElRef.current
		) {
			const {
				lastValue,
				selectionStart = lastValue ? `${lastValue}`.length : 0,
				selectionEnd = selectionStart,
				lastKey,
			} = this.state;
			const oldValue = `${lastValue}`;
			const newValue = `${this.inputElRef.current.value}`;
			const oldLength = `${oldValue}`.length;
			const newLength = `${newValue}`.length;

			if (oldValue === newValue) {
				if (newValue[selectionEnd - 1] === " " && lastKey === 8) {
					this.inputElRef.current.setSelectionRange(selectionEnd - 1, selectionEnd - 1);
				}

				return super.componentDidUpdate(prevProps, prevState);
			}

			const newPosition = Math.max(Math.min(newLength - oldLength + selectionEnd, newLength), 0);

			/* if (newValue.length > selectionEnd && newValue[selectionEnd - 1] === ' ' && lastKey !== 8) {
				newPosition++;
			} */

			this.setState({
				lastValue: this.inputElRef.current.value as unknown as Type,
			});

			this.inputElRef.current.setSelectionRange(newPosition, newPosition);
		}

		return super.componentDidUpdate(prevProps, prevState);
	}

	public componentWillUnmount(): void {
		super.componentWillUnmount();
		this.promisePool.cancel();
	}

	@autobind
	protected format(value: Type): string {
		const { mask } = this.props;

		if (typeof mask === "function") {
			return (mask as unknown as (value: Type) => string)(value);
		}

		if (typeof BmMask.maskFunctions[mask as string] === "function") {
			return BmMask.maskFunctions[mask as string](value, undefined);
		}

		if (mask) {
			Console.warn(`The "${String(mask)}" mask must is not exists!`);
		}

		return value === null || value === undefined ? "" : String(value);
	}

	@autobind
	protected parse(value: string): Type {
		const { mask } = this.props;

		if (typeof mask === "function") {
			return (mask as unknown as (v: string) => Type)(value);
		}

		if (typeof BmMask.unmaskFunctions[mask as string] === "function") {
			return BmMask.unmaskFunctions[mask as string](value, undefined) as Type;
		}

		if (mask) {
			Console.warn(`The "${String(mask)}" mask must is not exists!`);
		}

		return value as unknown as Type;
	}

	protected renderInput(fieldProps: FieldRenderProps<Type>, additionalClass?: string): ReactNode {
		const { lastValue } = this.state;
		const {
			input: { value },
		} = fieldProps;

		if (value !== undefined && lastValue === undefined) {
			this.promisePool.addTimeout(0).then(() => {
				this.setState({
					lastValue: value,
				});
			});
		}

		return super.renderInput(fieldProps, additionalClass);
	}

	protected getInputProps(fieldProps: FieldRenderProps<Type>): InputHTMLAttributes<HTMLInputElement> {
		return {
			...super.getInputProps(fieldProps),
			onSelect: (_event) => {
				this.setState({
					selectionStart: this.inputElRef.current.selectionStart,
					selectionEnd: this.inputElRef.current.selectionEnd,
				});
			},
			onKeyDown: (event) => {
				this.setState({
					lastKey: event.keyCode,
				});
			},
		};
	}
}
